import React, { useContext, useEffect, useRef, useState } from "react";
import { eventTypes, initialPrompt } from "utils/openAI";
import "./bot-meeting-room.css";
import CameraFeed from "components/CameraFeed";
import BotAgentSpeaker from "./component/BotAgentSpeaker";
import { v4 as uuidv4 } from "uuid";
import { CommonContext } from "hooks/CommonContext";
import axios from "axios";
import AiRoomHeader from "./component/AiRoomHeader";
import ChatMessage from "./component/ChatMessage";
import AiRoomFooter from "./component/AiRoomFooter";
import { Filter } from "bad-words";
import PropTypes from "prop-types";
import useIsMobile from "hooks/useIsMobile";
import { isSimilarQuestion } from "utils/commonUtils";

const { REACT_APP_OPENAI_API_URL, REACT_APP_CLOUD_FUNCTIONS_URL } = process.env;

const OpenAIBotInterviewRoom = ({ apiKey }) => {
  const { interviewDetails, setIsInterviewEnded } = useContext(CommonContext);
  const audioRef = useRef(null);
  const chatSectionRef = useRef(null);
  const [flicker, setFlicker] = useState(0);
  const [messages, setMessages] = useState([]);
  const [candidateDeltaTranscript, setCandidateDeltaTranscript] = useState("");
  const [botDeltaTranscript, setBotDeltaTranscript] = useState("");

  const audioContextRef = useRef(null);
  const pcRef = useRef(null);
  const dcRef = useRef(null);

  const screeningQAPairsRef = useRef([]);
  const regularQAPairsRef = useRef([]);

  // NEW: keep question/answer being built in refs instead of plain lets
  const currentCandidateAnswerRef = useRef("");
  const currentBotQuestionRef = useRef("");

  const badWordFilterHandler = (text) => {
    const filter = new Filter();
    const cleanText = filter.clean(text);
    return cleanText;
  };

  const submitConversationHaandler = (candidate_answer, openai_question) => {
    const sendData = {
      interview_guid: interviewDetails.interviewGuid,
      candidate_application_guid: interviewDetails.candidateApplicationGuid,
      openai_question: openai_question,
      candidate_answer: badWordFilterHandler(candidate_answer),
    };
    axios
      .post(REACT_APP_CLOUD_FUNCTIONS_URL + "/user_chats", sendData)
      .then((response) => { })
      .catch((error) => {
        console.error("Error sending data:", error);
      });
  };

  const disconnectFromRealtime = () => {
    if (pcRef.current) {
      pcRef.current.getSenders().forEach((sender) => {
        if (sender.track) {
          sender.track.stop();
        }
      });

      pcRef.current.close();
      pcRef.current = null;
    }
  };

  useEffect(() => {
    audioContextRef.current = new (window.AudioContext ||
      window.webkitAudioContext)();
    const audioContext = audioContextRef.current;
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const startRealtimeSession = async () => {
      try {
        screeningQAPairsRef.current = [];
        regularQAPairsRef.current = [];
        currentCandidateAnswerRef.current = "";
        currentBotQuestionRef.current = "";

        const sessionResponse = await fetch(
          REACT_APP_OPENAI_API_URL + "/sessions",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${apiKey}`,
              "Content-Type": "application/json",
            },
            body: initialPrompt(
              interviewDetails?.interviewQuestions,
              interviewDetails?.chatHistory ?? [],
              interviewDetails?.candidateDetails?.firstName,
              interviewDetails?.aiAgent?.agentLanguage,
              interviewDetails?.aiAgent?.voiceName,
              interviewDetails?.jobDetails?.questionPreference,
              interviewDetails?.jobDetails?.jobQuestions?.templates?.dataJson ??
              [],
              interviewDetails?.jobDetails?.currencyCode
            ),
          }
        );

        const sessionData = await sessionResponse.json();
        const clientSecret = sessionData.client_secret?.value;
        if (!clientSecret) throw new Error("Missing client_secret");

        const pc = new RTCPeerConnection();
        pcRef.current = pc;

        pc.ontrack = (event) => {
          if (audioRef.current) {
            audioRef.current.srcObject = event.streams[0];
            audioRef.current
              .play()
              .catch((err) => console.error("Audio playback error:", err));
          }
          const source = audioContext.createMediaStreamSource(event.streams[0]);
          source.connect(analyser);
          analyser.connect(audioContext.destination);
          animateVisualizer();
        };

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        stream.getTracks().forEach((track) => pc.addTrack(track, stream));

        const dc = pc.createDataChannel("oai-events");
        dcRef.current = dc;

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        const sdpRes = await fetch(
          REACT_APP_OPENAI_API_URL +
          "?model=gpt-4o-realtime-preview-2025-06-03",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${clientSecret}`,
              "Content-Type": "application/sdp",
            },
            body: offer.sdp,
          }
        );
        const answerSDP = await sdpRes.text();
        await pc.setRemoteDescription(
          new RTCSessionDescription({ type: "answer", sdp: answerSDP })
        );

        dc.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            if (data?.type === eventTypes.final && data?.part?.transcript) {
              const timestamp = new Date().toLocaleTimeString();

              if (currentCandidateAnswerRef.current.length > 0) {
                const cleanedAnswer = badWordFilterHandler(
                  currentCandidateAnswerRef.current.trim()
                );

                const templateQs =
                  interviewDetails?.jobDetails?.jobQuestions?.templates
                    ?.dataJson || [];

                let isScreeningQuestion = false;
                let canonicalQuestion = currentBotQuestionRef.current;

                // Only consider screening if preference is BOTH or MANUAL
                if (
                  (interviewDetails?.jobDetails?.questionPreference ===
                    "BOTH" ||
                    interviewDetails?.jobDetails?.questionPreference ===
                    "MANUAL") &&
                  Array.isArray(templateQs) &&
                  templateQs.length > 0
                ) {
                  // Collect which templates we've already used in screening
                  const usedTemplateSet = new Set(
                    screeningQAPairsRef.current
                      .map((pair) => pair.question || "")
                      .map((q) => q.toString().trim().toLowerCase())
                  );

                  // Find first unused template that matches this question
                  let matchedTemplate = null;
                  for (const tq of templateQs) {
                    const normTq = tq?.toString().trim().toLowerCase();
                    if (!normTq) continue;
                    if (usedTemplateSet.has(normTq)) continue;

                    if (isSimilarQuestion(currentBotQuestionRef.current, tq)) {
                      matchedTemplate = tq;
                      break;
                    }
                  }

                  if (matchedTemplate) {
                    isScreeningQuestion = true;
                    canonicalQuestion = matchedTemplate; // store template text
                  }
                }

                const qaPair = {
                  question: canonicalQuestion,
                  answer: cleanedAnswer,
                };

                if (isScreeningQuestion) {
                  // Screening Q&A: keep only for notes; do NOT send to user_chats
                  screeningQAPairsRef.current.push(qaPair);
                } else {
                  // Non-screening: push and store in main chats table
                  regularQAPairsRef.current.push(qaPair);
                  submitConversationHaandler(
                    cleanedAnswer,
                    canonicalQuestion
                  );
                }
              }

              // Now this final transcript becomes the *next* question
              currentBotQuestionRef.current = data.part.transcript;
              currentCandidateAnswerRef.current = "";

              const id = uuidv4().slice(0, 32);
              setMessages((prev) => [
                ...prev,
                {
                  id: id,
                  text: data?.part?.transcript,
                  sender: interviewDetails?.aiAgent?.displayName ?? "AI",
                  time: timestamp,
                },
              ]);

              const phrase1 = "thank you so much for your time today";
              const phrase2 = "thanks for your time";
              const transcript = data?.part?.transcript.toLowerCase();
              if (
                transcript.includes(phrase1) ||
                transcript.includes(phrase2)
              ) {
                setTimeout(() => {
                  setInterviewCompletionStatusHandler();
                }, 20000);
              }
              setBotDeltaTranscript("");
            }
            if (data?.type === eventTypes.botDeltaResponse && data?.delta) {
              setBotDeltaTranscript((prev) => prev + " " + data.delta);
            }

            if (
              data?.type === eventTypes.audioUserDeltaTranscription &&
              data?.delta
            ) {
              setCandidateDeltaTranscript(
                (prev) => prev + " " + badWordFilterHandler(data.delta)
              );
            }
            if (data?.type === eventTypes.userInput && data.transcript) {
              const timestamp = new Date().toLocaleTimeString();

              const id = uuidv4().slice(0, 32);
              setMessages((prev) => [
                ...prev,
                {
                  id: id,
                  text: badWordFilterHandler(data.transcript),
                  sender: "you",
                  time: timestamp,
                },
              ]);
              currentCandidateAnswerRef.current =
                currentCandidateAnswerRef.current + " " + data.transcript;
              setCandidateDeltaTranscript("");
            }
            if (chatSectionRef.current) {
              chatSectionRef.current.scrollTop =
                chatSectionRef.current.scrollHeight;
            }
          } catch (err) {
            console.error("Failed to parse OpenAI message:", err);
          }
        };
        dc.addEventListener("open", () => {
          sendSimulatedUserMessage("Hi");
        });
        dc.addEventListener("close", () => {
          console.log("data_channel.close");
        });
        dc.addEventListener("error", (err) => {
          console.log("data_channel.error");
        });
      } catch (err) {
        console.error("Realtime setup error:", err);
      }
    };

    startRealtimeSession();

    const animateVisualizer = () => {
      const animate = () => {
        analyser.getByteFrequencyData(dataArray);
        const avgFrequency =
          dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
        setFlicker(avgFrequency);
        requestAnimationFrame(animate);
      };
      animate();
    };
    // eslint-disable-next-line
  }, []);

  const sendClientEvent = (eventObj) => {
    if (dcRef.current && dcRef.current.readyState === "open") {
      dcRef.current.send(JSON.stringify(eventObj));
    } else {
      console.error(
        "Failed to send message - no data channel available",
        eventObj
      );
    }
  };

  const sendSimulatedUserMessage = (text) => {
    const id = uuidv4().slice(0, 32);
    sendClientEvent({
      type: "conversation.item.create",
      item: {
        id,
        type: "message",
        role: "user",
        content: [{ type: "input_text", text }],
      },
    });
    sendClientEvent({ type: "response.create" });
  };

  const setInterviewCompletionStatusHandler = async () => {
    const basePayload = {
      interview_guid: interviewDetails.interviewGuid,
      candidate_application_guid: interviewDetails.candidateApplicationGuid,
    };

    disconnectFromRealtime();
    setIsInterviewEnded(true);

    const formatScreeningNotes = (qaPairs) => {
      return qaPairs
        .map((pair, index) => {
          const q = pair.question?.trim() || "";
          const a = pair.answer?.trim() || "";
          return `Q${index + 1}: ${q}\nA${index + 1}: ${a}`;
        })
        .join("\n\n");
    };

    try {
      await axios.post(
        REACT_APP_CLOUD_FUNCTIONS_URL + "/set_interview_completion_status",
        basePayload
      );

      // Screening Q&A to Notes
      if (
        (interviewDetails?.jobDetails?.questionPreference === "BOTH" ||
          interviewDetails?.jobDetails?.questionPreference === "MANUAL") &&
        screeningQAPairsRef.current.length > 0
      ) {
        const formattedNotes = formatScreeningNotes(
          screeningQAPairsRef.current
        );

        const notesPayload = {
          candidateDetailsGuid: interviewDetails?.candidateDetails?.guid ?? "",
          note: formattedNotes,
        };

        await axios.post(
          REACT_APP_CLOUD_FUNCTIONS_URL + "/add_candidate_notes",
          notesPayload
        );
      }
    } catch (error) {
      console.error("Error sending data:", error);
    }
  };

  const isMobile = useIsMobile();
  return (
    <div className="meeting-container">
      <AiRoomHeader
        setInterviewCompletionStatusHandler={
          setInterviewCompletionStatusHandler
        }
      />
      <div className="bot-meeting-room">
        <div className="row   m-0 bot-inner-meeting-room">
          {/* Left Section */}
          <div className="col-12 col-md-5 px-3 ">
            <div
              className={`d-flex gap-2  bot-candidate-section ${isMobile ? "flex-row" : "flex-column"
                }`}
            >
              {/* Bot Speaker */}
              <div className="bot-speaker-box d-flex justify-content-center align-items-center">
                <BotAgentSpeaker flicker={flicker} />
              </div>
              {/* Candidate Camera */}
              <div className="candidate-camera-box  d-flex justify-content-center align-items-end mb-0   mb-xl-4">
                <div
                  className="camera-feed"
                  style={{ margin: isMobile ? "0" : "0 75px" }}
                >
                  <CameraFeed className="card-participant par_card" />
                </div>
              </div>
            </div>
          </div>
          {/* Right Section */}
          <div className="col-12 col-md-7">
            <div className=" d-flex flex-column justify-content-between chat-control-section">
              <div className="conversation-chat-section" ref={chatSectionRef}>
                {messages?.map((chat) => (
                  <ChatMessage key={chat.id} chat={chat} />
                ))}
                {botDeltaTranscript.length > 0 && (
                  <div className={`chat-bubble `}>
                    <h6 className={`sender-name  `}>
                      {interviewDetails?.aiAgent?.displayName}
                    </h6>
                    <p className={`chat-message`}>{botDeltaTranscript}</p>
                  </div>
                )}
                {candidateDeltaTranscript.length > 0 && (
                  <div className={`chat-bubble you`}>
                    <h6 className={`sender-name you-name text-end`}>You</h6>
                    <p className={`chat-message text-end`}>
                      {candidateDeltaTranscript}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <audio style={{ display: "none" }} ref={audioRef} autoPlay controls />
        <AiRoomFooter />
      </div>
    </div>
  );
};

OpenAIBotInterviewRoom.propTypes = {
  apiKey: PropTypes.any.isRequired,
};

export default OpenAIBotInterviewRoom;